import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { DeletionRequestService, DeletionRequest, DeletionType, DeletionStatus } from '../../../../../../../libs/shared/src/lib/services/deletion-request.service';
import { DeleteAccountModalComponent, DeleteAccountModalResult } from '../../../components/delete-account-modal/delete-account-modal.component';
import { ToastService } from '../../../services/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import { getErrorTranslationKey } from '../../../../../../../libs/shared/src/lib/i18n/error-translations';
import { formatDateTimeLocalized } from '../../../utils/date-utils';

@Component({
  selector: 'app-deletion-requests',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './deletion-requests.component.html',
  styleUrls: ['./deletion-requests.component.scss']
})
export class DeletionRequestsComponent implements OnInit {
  deletionRequests: DeletionRequest[] = [];
  isLoading = false;
  isCreatingRequest = false;

  constructor(
    private deletionRequestService: DeletionRequestService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadDeletionRequests();
  }

  formatDateTime(dateInput?: string): string {
    return formatDateTimeLocalized(dateInput, this.translocoService.getActiveLang());
  }

  loadDeletionRequests(): void {
    this.isLoading = true;
    this.deletionRequestService.getUserDeletionRequests(true).subscribe({
      next: (requests) => {
        this.deletionRequests = requests || [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading deletion requests:', error);
        this.isLoading = false;
        this.deletionRequests = [];

        let titleKey: string;
        let messageKey: string;

        if (error.status === 0) {
          titleKey = getErrorTranslationKey('CONNECTION_ERROR', 'title', 'common');
          messageKey = getErrorTranslationKey('CONNECTION_ERROR', 'message', 'common');
        } else if (error.status === 401) {
          titleKey = getErrorTranslationKey('SESSION_EXPIRED', 'title', 'common');
          messageKey = getErrorTranslationKey('SESSION_EXPIRED', 'message', 'common');
        } else {
          titleKey = getErrorTranslationKey('LOAD_REQUESTS_FAILED', 'title', 'accountDeletion');
          messageKey = getErrorTranslationKey('LOAD_REQUESTS_FAILED', 'message', 'accountDeletion');
        }

        this.toastService.error(
          this.translocoService.translate(titleKey),
          this.translocoService.translate(messageKey),
          true,
          5000
        );
      }
    });
  }

  openDeleteAccountModal(): void {
    const dialogRef = this.dialog.open(DeleteAccountModalComponent, {
      width: '600px',
      maxWidth: '90vw',
      disableClose: true,
      data: {}
    });

    dialogRef.afterClosed().subscribe((result: DeleteAccountModalResult) => {
      if (result?.confirmed) {
        this.createDeletionRequest(result.reason);
      }
    });
  }

  private createDeletionRequest(reason?: string): void {
    this.isCreatingRequest = true;

    this.deletionRequestService.createDeletionRequest({
      reason: reason || 'User requested account deletion',
      confirmDeletion: true
    },true).subscribe({
      next: (request) => {
        this.isCreatingRequest = false;
        this.toastService.success(
          this.translocoService.translate('common.success'),
          this.translocoService.translate('pages.settings.deletionRequests.toast.created'),
          true,
          5000
        );
        this.loadDeletionRequests(); // Refresh the list
      },
      error: (error) => {
        this.isCreatingRequest = false;
        console.error('Error creating deletion request:', error);

        let titleKey: string;
        let messageKey: string;

        // Check for specific error messages
        if (error.status === 401 || error.status === 403) {
          titleKey = getErrorTranslationKey('INVALID_PASSWORD', 'title', 'accountDeletion');
          messageKey = getErrorTranslationKey('INVALID_PASSWORD', 'message', 'accountDeletion');
        } else if (error.status === 400) {
          titleKey = getErrorTranslationKey('BAD_REQUEST', 'title', 'accountDeletion');
          messageKey = getErrorTranslationKey('BAD_REQUEST', 'message', 'accountDeletion');
        } else if (error.status === 409) {
          titleKey = getErrorTranslationKey('DUPLICATE_REQUEST', 'title', 'accountDeletion');
          messageKey = getErrorTranslationKey('DUPLICATE_REQUEST', 'message', 'accountDeletion');
        } else if (error.status === 0) {
          titleKey = getErrorTranslationKey('CONNECTION_ERROR', 'title', 'common');
          messageKey = getErrorTranslationKey('CONNECTION_ERROR', 'message', 'common');
        } else {
          titleKey = getErrorTranslationKey('CREATE_REQUEST_FAILED', 'title', 'accountDeletion');
          // Use backend error message if available, otherwise use default translation
          messageKey = error.error?.message || error.error?.error || error.message ||
                       getErrorTranslationKey('CREATE_REQUEST_FAILED', 'message', 'accountDeletion');
        }

        this.toastService.error(
          this.translocoService.translate(titleKey),
          // If messageKey is not a translation key (i.e., it's a backend error message), use it directly
          messageKey.includes('errors.') ? this.translocoService.translate(messageKey) : messageKey,
          true,
          8000
        );
      }
    });
  }

  cancelDeletionRequest(requestId: string): void {
    this.deletionRequestService.cancelDeletionRequest(requestId, true).subscribe({
      next: () => {
        this.toastService.success(
          this.translocoService.translate('common.success'),
          this.translocoService.translate('pages.settings.deletionRequests.toast.cancelled'),
          true,
          3000
        );
        this.loadDeletionRequests(); // Refresh the list
      },
      error: (error) => {
        console.error('Error cancelling deletion request:', error);
        const titleKey = getErrorTranslationKey('CANCEL_REQUEST_FAILED', 'title', 'accountDeletion');
        const messageKey = getErrorTranslationKey('CANCEL_REQUEST_FAILED', 'message', 'accountDeletion');
        this.toastService.error(
          this.translocoService.translate(titleKey),
          this.translocoService.translate(messageKey),
          true,
          5000
        );
      }
    });
  }

  getStatusColor(status?: string): string {
    switch (status) {
      case DeletionStatus.PENDING_VERIFICATION:
      case DeletionStatus.VERIFIED:
        return 'primary';
      case DeletionStatus.IN_PROGRESS:
        return 'accent';
      case DeletionStatus.COMPLETED:
        return 'basic';
      case DeletionStatus.REJECTED:
      case DeletionStatus.CANCELLED:
        return 'warn';
      default:
        return 'basic';
    }
  }

  // Returns a FontAwesome class suffix (e.g. 'fa-clock'). Consumers render
  // with <i class="fas {{ getStatusIcon(...) }}"></i>.
  getStatusIcon(status?: string): string {
    switch (status) {
      case DeletionStatus.PENDING_VERIFICATION:
        return 'fa-clock';
      case DeletionStatus.VERIFIED:
        return 'fa-user-shield';
      case DeletionStatus.IN_PROGRESS:
        return 'fa-hourglass';
      case DeletionStatus.COMPLETED:
        return 'fa-check-circle';
      case DeletionStatus.REJECTED:
      case DeletionStatus.CANCELLED:
        return 'fa-exclamation-circle';
      default:
        return 'fa-question-circle';
    }
  }

  canCancelRequest(request: DeletionRequest): boolean {
    return request.status === DeletionStatus.PENDING_VERIFICATION ||
           request.status === DeletionStatus.VERIFIED ||
           request.status === DeletionStatus.IN_PROGRESS;
  }

  get hasPendingOrProcessingRequest(): boolean {
    return this.deletionRequests.some(req =>
      req.status === DeletionStatus.PENDING_VERIFICATION ||
      req.status === DeletionStatus.VERIFIED ||
      req.status === DeletionStatus.IN_PROGRESS
    );
  }
}
